// Animar una lista que cambia de orden, con la técnica FLIP —
// ADR-2026-09-07-lanzamientos-reorden-animado-de-variantes.
//
// El problema: React repinta la lista en el orden nuevo y listo. No hay nada que
// transicionar, porque para el navegador no se movió nada — se redibujó otra
// cosa en el mismo lugar. Una `transition` de CSS no tiene de dónde agarrarse.
//
// FLIP le da ese agarre en tres pasos: se anota dónde estaba cada elemento
// ANTES, se deja que React pinte el orden nuevo, y antes de que se vea nada se
// le aplica a cada uno el desplazamiento INVERSO — o sea, se lo devuelve
// visualmente a donde estaba. Recién ahí se saca ese desplazamiento con una
// transición, y el elemento viaja de su lugar viejo al nuevo.
//
// Se mide con `offsetTop` y no con `getBoundingClientRect()`: el primero es
// relativo al documento y el segundo al viewport, así que si la página scrollea
// entre dos renders el segundo daría diferencias que no son movimientos y las
// cards se moverían solas. Y `offsetTop` ignora los `transform`, que es
// justamente lo que esta animación está usando.
import { useCallback, useLayoutEffect, useRef } from "react";

/** Cuánto dura el viaje. La curva es la misma que usa el resto de la pantalla. */
const MS = 260;
const CURVA = "cubic-bezier(0.22, 1, 0.36, 1)";

/** Menos de un píxel no es un movimiento: es el redondeo del layout. */
const MINIMO = 1;

/**
 * Cuánto hay que correr cada elemento para que PAREZCA seguir donde estaba.
 *
 * Es la diferencia al revés (`antes - ahora`) a propósito: un elemento que bajó
 * tiene que arrancar la animación desplazado hacia arriba. Solo devuelve los que
 * de verdad se movieron y estaban en las dos mediciones — uno que recién
 * aparece no tiene desde dónde viajar.
 */
export function desplazamientos(
  antes: ReadonlyMap<string, number>,
  ahora: ReadonlyMap<string, number>,
): Map<string, number> {
  const out = new Map<string, number>();
  ahora.forEach((posicion, id) => {
    const previa = antes.get(id);
    if (previa === undefined) return;
    const delta = previa - posicion;
    if (Math.abs(delta) >= MINIMO) out.set(id, delta);
  });
  return out;
}

/**
 * Devuelve un `ref` por id para poner en cada elemento de la lista. Cuando el
 * orden cambia, los que se movieron viajan a su lugar nuevo en vez de aparecer
 * ahí.
 *
 * No lleva lista de dependencias: se mide en CADA render, que es la única forma
 * de enterarse de un movimiento sin que el llamador tenga que avisar. Los
 * renders en los que nada se movió no hacen nada.
 */
export function useReordenAnimado() {
  const nodos = useRef(new Map<string, HTMLElement>());
  const posiciones = useRef(new Map<string, number>());

  useLayoutEffect(() => {
    const ahora = new Map<string, number>();
    nodos.current.forEach((el, id) => ahora.set(id, el.offsetTop));

    const mover = desplazamientos(posiciones.current, ahora);
    posiciones.current = ahora;
    if (mover.size === 0) return;

    // Con "reducir movimiento" el orden cambia igual, sin el viaje.
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

    mover.forEach((delta, id) => {
      const el = nodos.current.get(id);
      if (!el) return;
      el.style.transition = "none";
      el.style.transform = `translateY(${delta}px)`;
    });

    // Un frame después, para que el navegador llegue a pintar el estado
    // invertido: sacárselo en el mismo tick sería no haberlo puesto nunca.
    //
    // A propósito SIN cleanup que lo cancele. Un render que cayera entre este
    // efecto y el frame siguiente cancelaría el frame, y el efecto nuevo no
    // vería movimiento —`offsetTop` ignora los `transform`— así que nadie
    // limpiaría nada y las cards quedarían trabadas corridas, con la transición
    // en `none`. Dejándolo correr, lo peor que pasa con dos reordenamientos
    // seguidos es que el segundo frame limpie algo ya limpio.
    requestAnimationFrame(() => {
      mover.forEach((_, key) => {
        const el = nodos.current.get(key);
        if (!el) return;
        el.style.transition = `transform ${MS}ms ${CURVA}`;
        el.style.transform = "";
      });
    });
  });

  /**
   * El callback de cada id se cachea, y esto NO es una optimización: es lo que
   * hace que la animación exista.
   *
   * React limpia un `ref` cuya función cambió —lo llama con `null` y después con
   * el elemento— y eso pasa en el commit, ANTES del layout effect. Con una
   * función nueva en cada render, la limpieza borraba la posición anterior
   * justo antes de que se la fuera a comparar, y `desplazamientos` no encontraba
   * nunca de dónde venía nadie. Cacheado, la limpieza corre solo cuando la
   * variante se va de verdad.
   */
  const cache = useRef(new Map<string, (el: HTMLElement | null) => void>());

  return useCallback((id: string) => {
    const guardado = cache.current.get(id);
    if (guardado) return guardado;
    const fn = (el: HTMLElement | null) => {
      if (el) {
        nodos.current.set(id, el);
        return;
      }
      nodos.current.delete(id);
      // Y su posición, o al volver a montarse viajaría desde un lugar que ya no
      // significa nada.
      posiciones.current.delete(id);
      cache.current.delete(id);
    };
    cache.current.set(id, fn);
    return fn;
  }, []);
}
